import { Check } from "lucide-react";
import { COUNTIES, WASHINGTON_AREAS } from "@/lib/constants";

const COUNT_ON = [
  {
    title: "Qualified in both states.",
    body: "Julie is on the Oregon Department of Education's approved tester list and never tests students she's related to. She also tests Washington homeschoolers using state-approved tests.",
  },
  {
    title: "Everything handled for you.",
    body: "Julie schedules, administers and scores the test, then walks you through the results.",
  },
  {
    title: "A homeschool parent's perspective.",
    body: "She's glad to talk through what the results mean for next year, from curriculum choices to how to prepare.",
  },
];

export default function Program() {
  return (
    <section data-testid="program-section" className="bg-[#EBEBFA]">
      <div className="max-w-6xl mx-auto md:grid md:grid-cols-2">
        <div className="px-6 md:px-10 py-16 md:py-24 flex flex-col justify-center">
          <h2 className="text-3xl md:text-4xl text-[#1A243B] leading-snug mb-6">
            Meet Julie McConnell, your <span className="italic">Oregon homeschool tester</span>
          </h2>
          <p className="text-base md:text-lg text-[#2D3748] leading-relaxed mb-5">
            Julie has been testing Oregon &amp; Washington homeschoolers since 1996. She knows homeschooling from the inside, too: she taught her own children at home for nearly four decades, and now teaches her grandchildren.
          </p>
          <p className="text-base md:text-lg text-[#2D3748] leading-relaxed mb-8">
            Each spring she tests about 300 students, so she knows how to put a nervous child at ease. Her approach is patient, warm and unhurried. Children settle in, do their best work, and leave feeling capable rather than judged.
          </p>
          <p className="text-sm font-bold tracking-widest uppercase text-[#2D3748]/70 mb-5">What you can count on</p>
          <ul className="space-y-5 mb-10">
            {COUNT_ON.map((c) => (
              <li key={c.title} data-testid="program-point" className="flex items-start gap-3">
                <span className="mt-0.5 w-6 h-6 rounded-full bg-[#FFC107] flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 text-[#1A243B]" strokeWidth={3} />
                </span>
                <p className="text-base text-[#2D3748] leading-relaxed">
                  <span className="font-bold text-[#1A243B]">{c.title}</span> {c.body}
                </p>
              </li>
            ))}
          </ul>
          <div>
            <a data-testid="program-book-btn" href="#book" className="cta-pill inline-block bg-[#FFC107] text-[#1A243B] font-bold px-8 py-3.5 rounded-full">
              Book your testing date with Julie →
            </a>
          </div>
        </div>
        <div className="relative min-h-[300px] md:min-h-0">
          <img
            src="/images/study-desk.png"
            alt="A student working through a workbook at her home desk"
            data-testid="program-image"
            className="torn-right absolute inset-0 w-full h-full object-cover"
          />
        </div>
      </div>
      <div className="border-t border-[#1A243B]/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14">
          <p className="text-sm font-bold tracking-widest uppercase text-[#2D3748]/70 mb-6">Areas served</p>
          <div className="space-y-5 mb-8" data-testid="areas-served-line">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-display text-xl md:text-2xl text-[#1A243B]">Oregon:</span>
              {COUNTIES.map((c) => (
                <span key={c} data-testid={`county-pill-${c.toLowerCase()}`} className="bg-[#DCDCF5] text-[#1A243B] font-semibold text-sm px-5 py-2.5 rounded-full">
                  {c}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-display text-xl md:text-2xl text-[#1A243B]">Washington:</span>
              {WASHINGTON_AREAS.map((a) => (
                <span key={a} data-testid={`wa-pill-${a.toLowerCase().replace(/[\s/]+/g, "-")}`} className="bg-[#DCDCF5] text-[#1A243B] font-semibold text-sm px-5 py-2.5 rounded-full">
                  {a}
                </span>
              ))}
            </div>
          </div>
          <p className="text-base text-[#2D3748]" data-testid="travel-note">
            Outside these areas? We're happy to travel to you; a travel fee may apply.
          </p>
        </div>
      </div>
    </section>
  );
}
